import React from 'react';
import { Box, Typography } from '@mui/material';
import { useAuthorization } from 'framework/authorization';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import { EducationInput, useEducationService, DOCUMENT_TYPES, EDUCATION_OPTIONS } from 'domain/shared/employee';
import { CREATE_EDUCATION, EducationItemProps } from './types';
import moment from 'moment';
import { DATE_FORMAT } from 'framework/utils';
import { FormShowTemplate, useFormShowContext, DEFAULT_ERROR_MESSAGE } from 'framework/form';
import EditFormShowButton from '../../../shared/button/showEdit/EditFormShowButton';
import FormActionButtons from '../../../shared/button/showEdit/FormActionButtons';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import DeleteEducationAction from '../delete/DeleteEducationAction';

const EducationItem: React.FC<EducationItemProps> = ({
  education,
  onUpdate,
  onStartEdit,
  disabled = false,
  onCancelEdit,
}) => {
  const { isGranted } = useAuthorization();
  const { getValidationSchema, update, mapFromApi, deleteEducationById } = useEducationService();
  const { addMessage } = useMessageService();

  const educationType = EDUCATION_OPTIONS[education?.type as keyof typeof EDUCATION_OPTIONS] || education?.type || '-';
  const providerName = education?.providerName || '-';
  const graduationYear = education?.graduationYear || '';
  const specialization = education?.specialization || '-';
  const assignedQualification = education?.assignedQualification || '-';
  const document = education?.document;
  const attachmentsIds = document?.scansIds || [];

  const formatDate = (date: string) => {
    if (!date) return '';
    try {
      return moment(date).format(DATE_FORMAT.SIMPLE);
    } catch {
      return date;
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      await update(education.id, formData);
      return formData;
    } catch (error) {
      return addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const handleAfterSubmit = () => {
    onUpdate();
  };

  const handleDelete = async () => {
    try {
      await deleteEducationById(education.id);
      onUpdate();
    } catch (error) {
      return addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const renderEditForm = () => {
    return (
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <EducationInput source="" />
        </Box>
        <FormActionButtons onCancel={onCancelEdit} withDefaultToggle />
      </Box>
    );
  };

  const renderViewContent = () => {
    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box display="flex" flexDirection="column" sx={{ flex: 1, gap: 0.5 }}>
            <Typography variant="body1" color="primary">
              {educationType}
            </Typography>
            <Typography variant="body1">
              {providerName} ({graduationYear})
            </Typography>
            <Typography variant="body1">
              {assignedQualification} ({specialization})
            </Typography>
            {document && (
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                {DOCUMENT_TYPES[document.type as keyof typeof DOCUMENT_TYPES] || document.type} №{document.number}
                {document.dateOfIssue ? (
                  <>
                    <Typography variant="body1" color="textSecondary" component="span" mr={1.5} ml={1.5}>
                      Issued on
                    </Typography>
                    {formatDate(document.dateOfIssue)}
                  </>
                ) : (
                  ''
                )}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isGranted(CREATE_EDUCATION) && (
              <EditFormShowButton requiredPermission={[CREATE_EDUCATION]} disabled={disabled} onClick={onStartEdit} />
            )}
            <DeleteEducationAction onDelete={handleDelete} />
          </Box>
        </Box>

        {attachmentsIds.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <AttachmentsListField value={attachmentsIds} />
          </Box>
        )}
      </Box>
    );
  };

  const EducationItemContent = () => {
    const formShowContext = useFormShowContext();
    const { isEditMode } = formShowContext;

    return isEditMode ? renderEditForm() : renderViewContent();
  };

  return (
    <FormShowTemplate
      getValidationSchemaFunc={getValidationSchema}
      submitFormFunc={handleSubmit}
      afterSubmit={{
        execute: handleAfterSubmit,
      }}
      mapDataForForm={mapFromApi}
      getDataFunc={() => Promise.resolve(mapFromApi(education))}
    >
      <EducationItemContent />
    </FormShowTemplate>
  );
};

export default EducationItem;
